import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ArrowLeft, CalendarDays, Users, ClipboardList, Bell, BarChart3,
  Clock, MapPin, Phone, Mail, MoreVertical, ChevronDown, LogOut, Home,
  Mic, MicOff, Save, FileText, Euro, Wrench, ClipboardCheck, Truck, Check, Shield, Camera, Trash2, X, QrCode, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const STATUS_MAP = {
  scheduled: { label: 'Programmato', cls: 'bg-blue-50 text-blue-700' },
  in_progress: { label: 'In corso', cls: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completato', cls: 'bg-emerald-50 text-emerald-700' },
  cancelled: { label: 'Annullato', cls: 'bg-red-50 text-red-700' },
};

const TARIFFA_ORARIA = 40;

const aptSlotLabel = (apt) => {
  const ts = apt.time_slots || [];
  if (!apt.double_slot || ts.length < 2) return apt.time_slot;
  return `${ts[0].split('-')[0]}-${ts[ts.length - 1].split('-')[1]} (DOPPIO SLOT)`;
};

const PROVINCE_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
];

// ── Audio Recorder Hook ──
function useAudioRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      chunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => chunks.current.push(e.data);
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.current.start();
      setRecording(true);
    } catch {
      toast.error('Microfono non disponibile');
    }
  };

  const stop = () => {
    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };

  return { recording, audioUrl, start, stop, setAudioUrl };
}

function ClientAudio({ audioId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/admin/audio/${audioId}`, { responseType: 'blob' })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [audioId]);
  if (!url) return <p className="text-[11px] text-slate-400">Caricamento audio...</p>;
  return <audio controls src={url} className="w-full h-9" data-testid={`client-audio-${audioId}`} />;
}

function ClientProblem({ apt }) {
  if (!apt.problema && !apt.client_audio_id) return null;
  return (
    <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 space-y-2" data-testid={`client-problem-${apt.appointment_id}`}>
      <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Problema segnalato dal cliente</p>
      {apt.problema && <p className="text-xs text-slate-800 whitespace-pre-line">{apt.problema}</p>}
      {apt.client_audio_id && <ClientAudio audioId={apt.client_audio_id} />}
    </div>
  );
}

// ── Appointment Card with 3-row grid ──
function AppointmentCard({ apt, onStatusChange }) {
  const [notes, setNotes] = useState(apt.admin_notes || '');
  const [hours, setHours] = useState(apt.ore_lavorate || 0);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { recording, audioUrl, start, stop } = useAudioRecorder();

  const pb = apt.price_breakdown?.fixed || {};
  const isCheck = apt.service_type === 'check_generale';
  const costoUscita = 25;
  const costoKm = pb.distance_surcharge || 0;
  const distanzaKm = pb.distance_km || 0;
  const costoBase = isCheck ? (pb.check_generale_base || 100) : costoUscita;
  const costoFixture = isCheck ? ((pb.per_fixture || 10) * (pb.num_fixtures || 0)) : 0;
  const costoOre = !isCheck ? (hours * TARIFFA_ORARIA) : 0;
  const totale = isCheck ? (pb.total || apt.price_min || 0) : (costoUscita + costoKm + costoOre);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/appointments/${apt.appointment_id}/notes`, { admin_notes: notes });
      toast.success('Note salvate');
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const saveHours = async (h) => {
    setHours(h);
    try {
      await api.put(`/admin/appointments/${apt.appointment_id}/hours`, { ore_lavorate: h });
    } catch { /* silent */ }
  };

  return (
    <Card className="border-0 shadow-sm rounded-xl overflow-hidden" data-testid={`daily-apt-${apt.appointment_id}`}>
      {/* ── ROW 1: Appuntamento ── */}
      <div
        className="p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`apt-row1-${apt.appointment_id}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-50 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4 text-blue-500 mb-0.5" />
            <span className="text-xs font-bold text-blue-700">{apt.time_slot?.split('-')[0]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-bold text-sm text-slate-900 truncate">{apt.client_name || 'Cliente'}</p>
              <Badge className={`border-0 text-[10px] px-2 py-0.5 ${isCheck ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700'}`}>
                {isCheck ? 'Check' : 'Chiamata'}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1" data-testid={`apt-slot-${apt.appointment_id}`}><Clock className="w-3 h-3" />{aptSlotLabel(apt)}</span>
              {apt.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{apt.client_phone}</span>}
              {apt.address && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 flex-shrink-0" />{apt.address}</span>}
            </div>
            {(apt.num_finestre > 0 || apt.num_porte > 0) && (
              <p className="text-xs text-slate-400 mt-1">{apt.num_finestre} finestre, {apt.num_porte} porte</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg font-extrabold text-slate-900">{totale}€</span>
            <Badge className={`border-0 text-[10px] ${STATUS_MAP[apt.status]?.cls || 'bg-slate-50 text-slate-700'}`}>
              {STATUS_MAP[apt.status]?.label || apt.status}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                  <MoreVertical className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onStatusChange(apt.appointment_id, 'in_progress')}>In corso</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onStatusChange(apt.appointment_id, 'completed')}>Completato</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onStatusChange(apt.appointment_id, 'cancelled')} className="text-red-600">Annulla</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {expanded && (
        <>
          {/* ── ROW 2: Appunti / Audio ── */}
          <div className="border-t border-slate-100 p-4 bg-slate-50/30" data-testid={`apt-row2-${apt.appointment_id}`}>
            <div className="mb-3"><ClientProblem apt={apt} /></div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-blue-500" />
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Appunti Tecnico</p>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Cosa manca, cosa è stato fatto, materiali usati..."
              className="min-h-[80px] rounded-lg text-sm bg-white mb-3"
              data-testid={`apt-notes-${apt.appointment_id}`}
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={saveNotes}
                disabled={saving}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs gap-1"
                data-testid={`apt-save-notes-${apt.appointment_id}`}
              >
                <Save className="w-3 h-3" /> {saving ? 'Salvo...' : 'Salva Note'}
              </Button>
              {!recording ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={start}
                  className="rounded-lg text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`apt-rec-start-${apt.appointment_id}`}
                >
                  <Mic className="w-3 h-3" /> Registra Audio
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={stop}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs gap-1 animate-pulse"
                  data-testid={`apt-rec-stop-${apt.appointment_id}`}
                >
                  <MicOff className="w-3 h-3" /> Stop Registrazione
                </Button>
              )}
            </div>
            {audioUrl && (
              <div className="mt-3">
                <audio controls src={audioUrl} className="w-full h-10" data-testid={`apt-audio-${apt.appointment_id}`} />
              </div>
            )}
          </div>

          {/* ── ROW 3: Costi ── */}
          <div className="border-t border-slate-100 p-4" data-testid={`apt-row3-${apt.appointment_id}`}>
            <div className="flex items-center gap-2 mb-3">
              <Euro className="w-4 h-4 text-blue-500" />
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Dettaglio Costi</p>
            </div>

            {isCheck ? (
              /* ── CHECK GENERALE: solo totale predeterminato ── */
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <ClipboardCheck className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-[10px] font-bold text-blue-600 uppercase">Costo Check Generale</span>
                </div>
                <p className="text-2xl font-extrabold text-blue-700">{totale}€</p>
                <p className="text-[11px] text-blue-500 mt-1">Prezzo predeterminato (uscita, km e serramenti inclusi)</p>
                <p className="text-[11px] text-blue-500">Caparra: {apt.caparra || 0}€</p>
              </div>
            ) : (
              /* ── A CHIAMATA: uscita + km + ore lavorate ── */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Truck className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Uscita + KM</span>
                  </div>
                  <p className="text-lg font-extrabold text-slate-900">{costoUscita + costoKm}€</p>
                  <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                    <p>Uscita base: {costoUscita}€</p>
                    <p>Rimborso km A/R: {distanzaKm.toFixed(1)} km x 2 x 0,50€ → +{costoKm}€</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Wrench className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Ore Lavorate</span>
                  </div>
                  <p className="text-lg font-extrabold text-slate-900">{costoOre}€</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={hours}
                      onChange={(e) => saveHours(parseFloat(e.target.value) || 0)}
                      className="w-20 h-8 text-sm rounded-lg"
                      data-testid={`apt-hours-${apt.appointment_id}`}
                    />
                    <span className="text-[11px] text-slate-500">ore × {TARIFFA_ORARIA}€/h</span>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Euro className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[10px] font-bold text-blue-600 uppercase">Totale</span>
                  </div>
                  <p className="text-2xl font-extrabold text-blue-700">{totale}€</p>
                  <p className="text-[11px] text-blue-500 mt-1">Caparra: {apt.caparra || 0}€</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Province Map ──
function ProvinceMap({ mapData }) {
  const center = [45.39, 10.63];
  const colorMap = {};
  mapData.forEach((p, i) => { colorMap[p.province] = PROVINCE_COLORS[i % PROVINCE_COLORS.length]; });

  if (!mapData.length) {
    return (
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-12 text-center">
          <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessun appuntamento prenotato</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Province attive</p>
          <div className="flex flex-wrap gap-3">
            {mapData.map((p) => (
              <div key={p.province} className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorMap[p.province] }} />
                <span className="text-xs font-semibold text-slate-700 capitalize">{p.province}</span>
                <span className="text-xs text-slate-400">{p.total} app.</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
        <div style={{ height: 480 }}>
          <MapContainer center={center} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {mapData.map((p) => p.coords && (
              <CircleMarker
                key={p.province}
                center={[p.coords.lat, p.coords.lng]}
                radius={Math.max(14, Math.min(30, p.total * 8))}
                pathOptions={{ color: colorMap[p.province], fillColor: colorMap[p.province], fillOpacity: 0.4, weight: 2 }}
              >
                <Tooltip permanent direction="top" offset={[0, -10]}>
                  <span className="text-xs font-bold capitalize">{p.province} ({p.total})</span>
                </Tooltip>
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <p style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize', marginBottom: 8 }}>{p.province}</p>
                    {Object.entries(p.dates).map(([date, apts]) => (
                      <div key={date} style={{ marginBottom: 6 }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{date}</p>
                        {apts.map((a) => (
                          <p key={a.appointment_id} style={{ fontSize: 11, color: '#64748b', paddingLeft: 8 }}>
                            {a.time_slot} — {a.client_name || 'Cliente'} ({a.service_type === 'check_generale' ? 'Check' : 'Chiamata'})
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </Card>
    </div>
  );
}

// ── Booking Links Manager ──
function BookingLinksManager({ bookingLinks, setBookingLinks }) {
  const PROVINCE_DISPONIBILI = ['Brescia', 'Cremona', 'Mantova', 'Verona'];
  const [form, setForm] = useState({
    nome: '', cognome: '', telefono: '', email: '', address: '', provincia: '',
    service_type: 'check_generale', num_finestre: 5, num_porte: 2, note_admin: '',
  });
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const handleCreate = async () => {
    if (!form.nome || !form.telefono || !form.provincia) {
      toast.error('Nome, telefono e provincia obbligatori');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/admin/booking-links', form);
      setBookingLinks((prev) => [res.data, ...prev]);
      setForm({ nome: '', cognome: '', telefono: '', email: '', address: '', provincia: '', service_type: 'check_generale', num_finestre: 5, num_porte: 2, note_admin: '' });
      toast.success('Link creato!');
    } catch { toast.error('Errore creazione'); }
    finally { setCreating(false); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/prenota/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    toast.success('Link copiato!');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
      <Card className="border-0 shadow-sm rounded-xl h-fit">
        <CardContent className="p-5 space-y-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Crea link per il cliente</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nome *</label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome" className="rounded-lg text-sm" data-testid="bl-nome" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Cognome</label>
              <Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} placeholder="Cognome" className="rounded-lg text-sm" data-testid="bl-cognome" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Telefono *</label>
            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 333 ..." className="rounded-lg text-sm" data-testid="bl-telefono" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Indirizzo</label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via, numero, città" className="rounded-lg text-sm" data-testid="bl-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Provincia *</label>
              <select value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="bl-provincia">
                <option value="">Seleziona</option>
                {PROVINCE_DISPONIBILI.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Servizio</label>
              <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="bl-service">
                <option value="check_generale">Check Generale</option>
                <option value="una_tantum">A Chiamata</option>
              </select>
            </div>
          </div>
          {form.service_type === 'check_generale' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Finestre</label>
                <Input type="number" min="0" value={form.num_finestre} onChange={(e) => setForm({ ...form, num_finestre: parseInt(e.target.value) || 0 })} className="rounded-lg text-sm" data-testid="bl-finestre" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Porte</label>
                <Input type="number" min="0" value={form.num_porte} onChange={(e) => setForm({ ...form, num_porte: parseInt(e.target.value) || 0 })} className="rounded-lg text-sm" data-testid="bl-porte" />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Note interne</label>
            <Input value={form.note_admin} onChange={(e) => setForm({ ...form, note_admin: e.target.value })} placeholder="Note visibili solo a te" className="rounded-lg text-sm" data-testid="bl-note" />
          </div>
          <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} disabled={creating} data-testid="bl-create-btn">
            {creating ? 'Creo...' : 'Genera Link'}
          </Button>
        </CardContent>
      </Card>

      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Link generati</p>
        {bookingLinks.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-8 text-center">
              <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Nessun link creato</p>
              <p className="text-xs text-slate-400 mt-1">Compila il form e genera un link da inviare al cliente</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {bookingLinks.map((bl) => (
              <Card key={bl.token} className="border-0 shadow-sm rounded-xl" data-testid={`bl-${bl.token}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900">{bl.nome} {bl.cognome}</p>
                      <Badge className={`border-0 text-[10px] ${bl.status === 'booked' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {bl.status === 'booked' ? 'Prenotato' : 'In attesa'}
                      </Badge>
                      <Badge className={`border-0 text-[10px] ${bl.service_type === 'check_generale' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}>
                        {bl.service_type === 'check_generale' ? 'Check' : 'Chiamata'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg text-xs"
                        onClick={() => copyLink(bl.token)}
                        data-testid={`bl-copy-${bl.token}`}
                      >
                        {copiedToken === bl.token ? <Check className="w-3 h-3 mr-1" /> : <ClipboardList className="w-3 h-3 mr-1" />}
                        {copiedToken === bl.token ? 'Copiato!' : 'Copia Link'}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    {bl.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{bl.telefono}</span>}
                    {bl.provincia && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{bl.provincia}</span>}
                    <span className="text-slate-400">{new Date(bl.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                  <div className="mt-2 bg-slate-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-slate-500 font-mono truncate">{window.location.origin}/prenota/{bl.token}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tech View (Mobile-Friendly) ──
function TechView() {
  const [techDate, setTechDate] = useState(new Date());
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [techNotes, setTechNotes] = useState({});
  const [recId, setRecId] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const [recAudio, setRecAudio] = useState({});
  const [photos, setPhotos] = useState({}); // { aptId: [{photo_id, storage_path, created_at}] }
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const uploadAptRef = useRef(null);

  const loadPhotos = async (aptId) => {
    try {
      const res = await api.get(`/admin/appointments/${aptId}/photos`);
      setPhotos((prev) => ({ ...prev, [aptId]: res.data }));
    } catch { /* ignore */ }
  };

  const loadDay = async (d) => {
    setLoading(true);
    try {
      const dateStr = format(d, 'yyyy-MM-dd');
      const res = await api.get(`/admin/appointments/daily?date=${dateStr}`);
      setDayData(res.data);
      const notes = {};
      const allAptIds = [];
      Object.values(res.data.zones || {}).flat().forEach((apt) => {
        notes[apt.appointment_id] = apt.tech_notes || '';
        allAptIds.push(apt.appointment_id);
      });
      setTechNotes(notes);
      // Load photos for all appointments
      allAptIds.forEach((id) => loadPhotos(id));
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDay(techDate); }, [techDate]);

  const navigateDay = (dir) => {
    setTechDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + dir);
      return next;
    });
  };

  const saveTechUpdate = async (aptId, fields) => {
    setSavingId(aptId);
    try {
      await api.put(`/admin/appointments/${aptId}/tech-update`, fields);
      toast.success('Salvato');
      loadDay(techDate);
    } catch { toast.error('Errore salvataggio'); }
    finally { setSavingId(null); }
  };

  const startRec = async (aptId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      mediaRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        const reader = new FileReader();
        reader.onloadend = () => {
          const b64 = reader.result;
          setRecAudio((prev) => ({ ...prev, [aptId]: b64 }));
          saveTechUpdate(aptId, { tech_audio: b64 });
        };
        reader.readAsDataURL(blob);
      };
      mediaRef.current.start();
      setRecId(aptId);
    } catch { toast.error('Microfono non disponibile'); }
  };

  const stopRec = () => {
    if (mediaRef.current?.state === 'recording') {
      mediaRef.current.stop();
      setRecId(null);
    }
  };

  const handleUploadPhoto = async (aptId, file) => {
    if (!file) return;
    setUploadingPhoto(aptId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/admin/appointments/${aptId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadPhotos(aptId);
      toast.success('Foto caricata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload foto');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (aptId, photoId) => {
    try {
      await api.delete(`/admin/photos/${photoId}`);
      setPhotos((prev) => ({
        ...prev,
        [aptId]: (prev[aptId] || []).filter((p) => p.photo_id !== photoId),
      }));
      toast.success('Foto eliminata');
    } catch { toast.error('Errore eliminazione'); }
  };

  const triggerFileInput = (aptId) => {
    uploadAptRef.current = aptId;
    fileInputRef.current?.click();
  };

  const onFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file && uploadAptRef.current) {
      handleUploadPhoto(uploadAptRef.current, file);
    }
    e.target.value = '';
  };

  const VISIT_OPTIONS = [
    { value: 'in_corso', label: 'In Corso', color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { value: 'completato', label: 'Completato', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { value: 'da_ritornare', label: 'Da Ritornare', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  ];
  const PAY_OPTIONS = [
    { value: 'pagato', label: 'Pagato', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { value: 'non_pagato', label: 'Non Pagato', color: 'bg-red-100 text-red-700 border-red-300' },
  ];

  const allApts = dayData ? Object.values(dayData.zones || {}).flat().sort((a, b) => (a.time_slot || '').localeCompare(b.time_slot || '')) : [];

  return (
    <div data-testid="tech-view" className="max-w-2xl mx-auto">
      {/* Hidden file input for photo capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileSelected}
        data-testid="photo-file-input"
      />

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)}>
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${api.defaults.baseURL}/admin/photos/${previewPhoto.photo_id}`}
              alt="Preview"
              className="max-w-full max-h-[85vh] rounded-xl object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                onClick={() => { handleDeletePhoto(previewPhoto.aptId, previewPhoto.photo_id); setPreviewPhoto(null); }}
                className="bg-red-600 text-white rounded-xl px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Elimina
              </button>
              <button onClick={() => setPreviewPhoto(null)} className="bg-white/90 text-slate-800 rounded-xl px-3 py-1.5 text-xs font-semibold">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Navigation */}
      <div className="flex items-center justify-between mb-5 sticky top-0 bg-white z-10 py-2">
        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigateDay(-1)} data-testid="tech-prev-day">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="text-center">
          <p className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {format(techDate, 'EEEE d MMMM', { locale: it })}
          </p>
          <p className="text-xs text-slate-400">{allApts.length} appuntament{allApts.length !== 1 ? 'i' : 'o'}</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigateDay(1)} data-testid="tech-next-day">
          <ArrowLeft className="w-4 h-4 rotate-180" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : allApts.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-10 text-center">
            <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nessun appuntamento per oggi</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {allApts.map((apt) => {
            const aptId = apt.appointment_id;
            const visitSt = apt.visit_status || (apt.status === 'completed' ? 'completato' : apt.status === 'to_return' ? 'da_ritornare' : 'in_corso');
            const paySt = apt.payment_status_tech || (apt.payment_status === 'paid' ? 'pagato' : 'non_pagato');
            const mapsUrl = apt.client_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apt.client_address)}` : (apt.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apt.address)}` : null);
            const audioSrc = recAudio[aptId] || apt.tech_audio || null;

            return (
              <Card key={aptId} className="border-0 shadow-md rounded-2xl overflow-hidden" data-testid={`tech-apt-${aptId}`}>
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-300" />
                    <span className="text-sm font-bold text-white">{aptSlotLabel(apt) || '—'}</span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    apt.service_type === 'check_generale' ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'
                  }`}>
                    {apt.service_type === 'check_generale' ? 'Check Generale' : 'A Chiamata'}
                  </span>
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Client Info */}
                  <div className="space-y-2">
                    <p className="text-base font-bold text-slate-900">{apt.client_name || 'Cliente'}</p>
                    <div className="flex flex-col gap-1.5">
                      {(apt.client_address || apt.address) && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-slate-600">{apt.client_address || apt.address}</p>
                        </div>
                      )}
                      {apt.client_phone && (
                        <a href={`tel:${apt.client_phone}`} className="flex items-center gap-2 text-blue-600" data-testid={`tech-call-${aptId}`}>
                          <Phone className="w-3.5 h-3.5" />
                          <span className="text-xs font-semibold underline">{apt.client_phone}</span>
                        </a>
                      )}
                    </div>
                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-1">
                      {mapsUrl && (
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-blue-100 transition-colors"
                          data-testid={`tech-maps-${aptId}`}
                        >
                          <Truck className="w-3.5 h-3.5" /> Naviga
                        </a>
                      )}
                      {apt.client_phone && (
                        <a href={`tel:${apt.client_phone}`}
                          className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5" /> Chiama
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Province & Details */}
                  <div className="flex flex-wrap gap-1.5">
                    {apt.province && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{apt.province}</span>
                    )}
                    {apt.num_finestre > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500">{apt.num_finestre} finestre</span>}
                    {apt.num_porte > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500">{apt.num_porte} porte</span>}
                  </div>

                  <ClientProblem apt={apt} />

                  {/* Admin Notes (read-only) */}
                  {apt.note_admin && (
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                      <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">Note Admin</p>
                      <p className="text-xs text-amber-800">{apt.note_admin}</p>
                    </div>
                  )}

                  {/* Tech Notes (editable) */}
                  <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Note Tecnico
                    </p>
                    <textarea
                      className="w-full text-xs rounded-lg border border-slate-200 p-2 resize-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      rows={2}
                      placeholder="Scrivi note sull'intervento..."
                      value={techNotes[aptId] ?? ''}
                      onChange={(e) => setTechNotes((prev) => ({ ...prev, [aptId]: e.target.value }))}
                      data-testid={`tech-notes-input-${aptId}`}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm" variant="outline"
                        className="rounded-lg text-xs flex-1"
                        disabled={savingId === aptId}
                        onClick={() => saveTechUpdate(aptId, { tech_notes: techNotes[aptId] || '' })}
                        data-testid={`tech-save-notes-${aptId}`}
                      >
                        <Save className="w-3 h-3 mr-1" /> Salva Note
                      </Button>
                      {recId === aptId ? (
                        <Button size="sm" className="rounded-lg text-xs bg-red-600 hover:bg-red-700 text-white" onClick={stopRec} data-testid={`tech-stop-rec-${aptId}`}>
                          <MicOff className="w-3 h-3 mr-1" /> Stop
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => startRec(aptId)} data-testid={`tech-start-rec-${aptId}`}>
                          <Mic className="w-3 h-3 mr-1" /> Audio
                        </Button>
                      )}
                    </div>
                    {audioSrc && (
                      <audio controls src={audioSrc} className="w-full h-9 mt-1" data-testid={`tech-audio-${aptId}`} />
                    )}
                  </div>

                  {/* Photos */}
                  <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                        <Camera className="w-3 h-3" /> Foto Intervento ({(photos[aptId] || []).length}/5)
                      </p>
                      {(photos[aptId] || []).length < 5 && (
                        <Button
                          size="sm" variant="outline"
                          className="rounded-lg text-xs h-7"
                          disabled={uploadingPhoto === aptId}
                          onClick={() => triggerFileInput(aptId)}
                          data-testid={`tech-add-photo-${aptId}`}
                        >
                          {uploadingPhoto === aptId ? (
                            <><div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-1" /> Caricamento...</>
                          ) : (
                            <><Camera className="w-3 h-3 mr-1" /> Scatta / Allega</>
                          )}
                        </Button>
                      )}
                    </div>
                    {(photos[aptId] || []).length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {(photos[aptId] || []).map((photo) => (
                          <div key={photo.photo_id} className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-white">
                            <img
                              src={`${api.defaults.baseURL}/admin/photos/${photo.photo_id}`}
                              alt="Foto intervento"
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => setPreviewPhoto({ ...photo, aptId })}
                              data-testid={`tech-photo-${photo.photo_id}`}
                            />
                            <button
                              onClick={() => handleDeletePhoto(aptId, photo.photo_id)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`tech-delete-photo-${photo.photo_id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic text-center py-3">Nessuna foto. Scatta o allega una foto dell'intervento.</p>
                    )}
                  </div>

                  {/* Visit Status */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Stato Intervento</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {VISIT_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => saveTechUpdate(aptId, { visit_status: opt.value })}
                          disabled={savingId === aptId}
                          className={`px-2 py-2 rounded-xl text-[11px] font-bold border transition-all ${
                            visitSt === opt.value ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                          }`}
                          data-testid={`tech-visit-${opt.value}-${aptId}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cost Breakdown for Technician */}
                  <div className="bg-emerald-50 rounded-xl p-3 space-y-2 border border-emerald-200">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                      <Euro className="w-3 h-3" /> Costo da Comunicare al Cliente
                    </p>
                    {apt.service_type === 'check_generale' ? (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-600">Totale Check Generale</span>
                        <span className="text-sm font-extrabold text-emerald-700">
                          €{apt.price_breakdown?.fixed?.total ? Number(apt.price_breakdown.fixed.total).toFixed(0) : (apt.price_breakdown?.min ?? '—')}
                        </span>
                      </div>
                    ) : apt.price_breakdown?.fixed ? (
                      <div className="space-y-1">
                        {apt.price_breakdown.fixed.uscita != null && (
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-600">Uscita tecnico ({apt.price_breakdown.fixed.uscita_km || 0} km)</span>
                            <span className="font-semibold">€{Number(apt.price_breakdown.fixed.uscita).toFixed(0)}</span>
                          </div>
                        )}
                        {apt.price_breakdown.fixed.blocco_intervento != null && (
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-600">Manodopera (min. {apt.price_breakdown.fixed.blocco_ore || 1}h)</span>
                            <span className="font-semibold">€{Number(apt.price_breakdown.fixed.blocco_intervento).toFixed(0)}</span>
                          </div>
                        )}
                        <div className="border-t border-emerald-300 pt-1 mt-1 flex justify-between">
                          <span className="text-xs font-bold text-slate-800">Totale fisso</span>
                          <span className="text-sm font-extrabold text-emerald-700">€{Number(apt.price_breakdown.fixed.total || 0).toFixed(0)}</span>
                        </div>
                        {apt.price_breakdown?.variable?.issues_max > 0 && (
                          <p className="text-[10px] text-amber-600 mt-1">+ eventuali problematiche: fino a €{apt.price_breakdown.variable.issues_max}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Preventivo non disponibile</p>
                    )}
                  </div>

                  {/* Payment Status */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <Euro className="w-3 h-3" /> Pagamento
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {PAY_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => saveTechUpdate(aptId, { payment_status_tech: opt.value })}
                          disabled={savingId === aptId}
                          className={`px-2 py-2 rounded-xl text-[11px] font-bold border transition-all ${
                            paySt === opt.value ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                          }`}
                          data-testid={`tech-pay-${opt.value}-${aptId}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {apt.caparra > 0 && (
                      <p className="text-xs text-slate-500">Caparra: <span className="font-bold text-slate-700">€{apt.caparra}</span></p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Weekly View ──
const WEEKDAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];
const PROV_COLORS = {
  brescia: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  cremona: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' },
  mantova: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' },
  verona: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-300', dot: 'bg-violet-500' },
};
const ALL_TIME_SLOTS = ['08:00-10:00', '10:00-12:00', '13:00-15:00', '15:00-17:00'];

function WeeklyView() {
  const [weekData, setWeekData] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  const [loading, setLoading] = useState(false);

  const loadWeek = async (start) => {
    setLoading(true);
    try {
      const dateStr = format(start, 'yyyy-MM-dd');
      const res = await api.get(`/admin/appointments/weekly?start_date=${dateStr}`);
      setWeekData(res.data);
    } catch (err) {
      toast.error('Errore caricamento settimana');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWeek(weekStart); }, [weekStart]);

  const navigateWeek = (dir) => {
    setWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + dir * 7);
      return next;
    });
  };

  const isSlotBlocked = (day, slot) => {
    if (!day) return false;
    const bs = day.blocked_slots;
    if (Array.isArray(bs) && bs.length === 0) return true; // full day blocked
    if (Array.isArray(bs) && bs.includes(slot)) return true;
    return false;
  };

  const isFullDayBlocked = (day) => {
    return day && Array.isArray(day.blocked_slots) && day.blocked_slots.length === 0;
  };

  const getAptForSlot = (day, slot) => {
    if (!day?.appointments) return null;
    return day.appointments.find((a) => a.time_slot === slot || (a.time_slots || []).includes(slot));
  };

  const provStyle = (prov) => PROV_COLORS[prov?.toLowerCase()] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' };

  return (
    <div data-testid="weekly-view">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigateWeek(-1)} data-testid="week-prev">
          <ArrowLeft className="w-4 h-4 mr-1" /> Settimana precedente
        </Button>
        <div className="text-center">
          <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {weekData ? `${format(new Date(weekData.week_start), 'd MMM', { locale: it })} — ${format(new Date(new Date(weekData.week_start).getTime() + 4 * 86400000), 'd MMM yyyy', { locale: it })}` : '...'}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigateWeek(1)} data-testid="week-next">
          Settimana successiva <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : weekData ? (
        <>
          {/* Province Legend */}
          <div className="flex flex-wrap gap-3 mb-4">
            {Object.entries(PROV_COLORS).map(([prov, c]) => (
              <div key={prov} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                <span className="text-xs font-medium text-slate-600">{prov.charAt(0).toUpperCase() + prov.slice(1)}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="text-xs font-medium text-slate-600">Bloccato</span>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-5 gap-3">
            {weekData.days.map((day, idx) => {
              const dateObj = new Date(day.date + 'T00:00:00');
              const isToday = format(new Date(), 'yyyy-MM-dd') === day.date;
              const fullBlocked = isFullDayBlocked(day);
              const prov = day.assigned_province;
              const pc = provStyle(prov);
              const totalApts = day.appointments?.length || 0;

              return (
                <div key={day.date} className="min-w-0" data-testid={`week-day-${day.date}`}>
                  {/* Day Header */}
                  <div className={`rounded-t-xl p-3 ${isToday ? 'bg-blue-600 text-white' : 'bg-white border border-b-0 border-slate-200'}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${isToday ? 'text-blue-200' : 'text-slate-400'}`}>
                      {WEEKDAY_NAMES[idx]}
                    </p>
                    <p className={`text-lg font-extrabold ${isToday ? 'text-white' : 'text-slate-900'}`}>
                      {dateObj.getDate()}
                    </p>
                    <p className={`text-[10px] ${isToday ? 'text-blue-200' : 'text-slate-400'}`}>
                      {dateObj.toLocaleDateString('it-IT', { month: 'short' })}
                    </p>
                  </div>

                  {/* Day Body */}
                  <div className={`rounded-b-xl border border-t-0 p-2 space-y-1.5 min-h-[260px] ${
                    fullBlocked ? 'bg-red-50/50 border-red-200' : prov ? `${pc.bg} ${pc.border}` : 'bg-white border-slate-200'
                  }`}>
                    {/* Province Badge */}
                    {prov && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${pc.bg}`}>
                        <MapPin className={`w-3 h-3 ${pc.text}`} />
                        <span className={`text-[10px] font-bold ${pc.text}`}>{prov.charAt(0).toUpperCase() + prov.slice(1)}</span>
                      </div>
                    )}

                    {fullBlocked ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Shield className="w-5 h-5 text-red-400 mb-1" />
                        <p className="text-[10px] font-bold text-red-500 uppercase">Bloccato</p>
                        {day.reason && <p className="text-[9px] text-red-400 mt-0.5">{day.reason}</p>}
                      </div>
                    ) : (
                      <>
                        {ALL_TIME_SLOTS.map((slot) => {
                          const blocked = isSlotBlocked(day, slot);
                          const apt = getAptForSlot(day, slot);
                          const aptProv = apt?.province?.toLowerCase();
                          const aptPc = provStyle(aptProv);

                          if (blocked && !apt) {
                            return (
                              <div key={slot} className="rounded-lg bg-red-50 border border-red-200 p-1.5 opacity-60">
                                <p className="text-[9px] font-medium text-red-400 line-through">{slot}</p>
                              </div>
                            );
                          }

                          if (apt) {
                            return (
                              <div key={slot} className={`rounded-lg border-l-[3px] ${aptPc.border} ${aptPc.bg} p-1.5`} data-testid={`week-apt-${apt.appointment_id}`}>
                                <div className="flex items-center justify-between">
                                  <p className="text-[9px] font-bold text-slate-500">{slot.split('-')[0]}{apt.double_slot ? ' · DOPPIO SLOT' : ''}</p>
                                  <div className={`w-1.5 h-1.5 rounded-full ${aptPc.dot}`} />
                                </div>
                                <p className="text-[10px] font-semibold text-slate-800 truncate">{apt.client_name || 'Cliente'}</p>
                                <p className="text-[9px] text-slate-500 truncate">{apt.zone || apt.province}</p>
                              </div>
                            );
                          }

                          return (
                            <div key={slot} className="rounded-lg border border-dashed border-slate-200 p-1.5">
                              <p className="text-[9px] text-slate-300 font-medium">{slot}</p>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Summary */}
                    {!fullBlocked && totalApts > 0 && (
                      <div className="pt-1 border-t border-slate-100">
                        <p className="text-[9px] text-slate-400 font-medium">{totalApts} appuntament{totalApts > 1 ? 'i' : 'o'}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Calendar Management ──
function CalendarManagement({ blockedDates, setBlockedDates }) {
  const [calDate, setCalDate] = useState(new Date());
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [blockMode, setBlockMode] = useState('day'); // 'day' or 'slots'
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [provinceProcessing, setProvinceProcessing] = useState(false);

  const ALL_SLOTS = ['08:00-10:00', '10:00-12:00', '13:00-15:00', '15:00-17:00'];
  const PROVINCES = ['brescia', 'cremona', 'mantova', 'verona'];

  const getBlockedEntry = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.find((d) => d.date === dateStr);
  };

  const isBlocked = (date) => !!getBlockedEntry(date);
  const isFullDayBlocked = (date) => { const e = getBlockedEntry(date); return e && e.blocked_slots !== undefined && e.blocked_slots !== null && Array.isArray(e.blocked_slots) && e.blocked_slots.length === 0 && !e.assigned_province; };
  const isFullDayBlockedAny = (date) => { const e = getBlockedEntry(date); return e && Array.isArray(e.blocked_slots) && e.blocked_slots.length === 0; };
  const isPartialBlocked = (date) => { const e = getBlockedEntry(date); return e && e.blocked_slots && e.blocked_slots.length > 0; };
  const hasProvince = (date) => { const e = getBlockedEntry(date); return e && e.assigned_province; };

  const isValidDay = (date) => {
    const wd = date.getDay();
    return wd >= 1 && wd <= 5;
  };

  const handleBlock = async () => {
    if (!calDate) return;
    const dateStr = format(calDate, 'yyyy-MM-dd');
    setProcessing(true);
    try {
      const slotsToBlock = blockMode === 'slots' ? selectedSlots : [];
      const res = await api.post('/admin/blocked-dates', { date: dateStr, reason, blocked_slots: slotsToBlock });
      setBlockedDates((prev) => {
        const filtered = prev.filter((d) => d.date !== dateStr);
        return [...filtered, res.data].sort((a, b) => a.date.localeCompare(b.date));
      });
      setReason('');
      setSelectedSlots([]);
      toast.success(blockMode === 'day' ? `${dateStr} bloccato` : `Fasce bloccate per ${dateStr}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnblock = async (dateStr, slot) => {
    try {
      const url = slot ? `/admin/blocked-dates/${dateStr}?slot=${encodeURIComponent(slot)}` : `/admin/blocked-dates/${dateStr}`;
      await api.delete(url);
      if (slot) {
        setBlockedDates((prev) => prev.map((d) => {
          if (d.date !== dateStr) return d;
          const newSlots = (d.blocked_slots || []).filter((s) => s !== slot);
          return newSlots.length === 0
            ? (d.assigned_province ? { ...d, blocked_slots: undefined } : null)
            : { ...d, blocked_slots: newSlots };
        }).filter(Boolean));
      } else {
        setBlockedDates((prev) => {
          const entry = prev.find((d) => d.date === dateStr);
          if (entry?.assigned_province) {
            return prev.map((d) => d.date === dateStr ? { ...d, blocked_slots: undefined } : d);
          }
          return prev.filter((d) => d.date !== dateStr);
        });
      }
      toast.success(slot ? `Fascia ${slot} sbloccata` : `${dateStr} sbloccato`);
    } catch (err) { toast.error('Errore'); }
  };

  const handleProvinceChange = async (province) => {
    if (!calDate) return;
    const currentProvince = selectedEntry?.assigned_province || '';
    if (province === currentProvince) return;
    const dateStr = format(calDate, 'yyyy-MM-dd');
    setProvinceProcessing(true);
    try {
      const res = await api.put('/admin/day-province', { date: dateStr, province: province || '' });
      if (res.data) {
        setBlockedDates((prev) => {
          const filtered = prev.filter((d) => d.date !== dateStr);
          if (res.data.assigned_province || (res.data.blocked_slots !== undefined && res.data.blocked_slots !== null)) {
            return [...filtered, res.data].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
          }
          return filtered;
        });
      }
      toast.success(province ? `Provincia ${province} assegnata a ${dateStr}` : `Provincia rimossa da ${dateStr}`);
    } catch (err) {
      console.error('Province error:', err);
      const msg = err.response?.data?.detail || err.message || 'Errore sconosciuto';
      toast.error(`Errore provincia: ${msg} (${err.response?.status || 'network'})`);
    } finally {
      setProvinceProcessing(false);
    }
  };

  const toggleSlot = (slot) => {
    setSelectedSlots((prev) => prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]);
  };

  const selectedEntry = calDate ? getBlockedEntry(calDate) : null;
  const selectedIsValid = calDate ? isValidDay(calDate) : false;

  const PROVINCE_COLORS = {
    brescia: { bg: '#DBEAFE', text: '#1D4ED8', border: '#93C5FD' },
    cremona: { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
    mantova: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
    verona: { bg: '#EDE9FE', text: '#5B21B6', border: '#C4B5FD' },
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Seleziona data</p>
          <Calendar
            mode="single"
            selected={calDate}
            onSelect={(d) => { setCalDate(d); setSelectedSlots([]); setBlockMode('day'); }}
            locale={it}
            className="w-full"
            modifiers={{
              fullBlocked: (date) => isFullDayBlockedAny(date),
              partialBlocked: (date) => isPartialBlocked(date),
              provinceAssigned: (date) => hasProvince(date) && !isFullDayBlockedAny(date) && !isPartialBlocked(date),
            }}
            modifiersStyles={{
              fullBlocked: { backgroundColor: '#FEE2E2', color: '#DC2626', fontWeight: 700 },
              partialBlocked: { backgroundColor: '#FEF3C7', color: '#D97706', fontWeight: 700 },
              provinceAssigned: { backgroundColor: '#DBEAFE', color: '#1D4ED8', fontWeight: 700 },
            }}
            data-testid="blocked-calendar"
          />
          {calDate && selectedIsValid && (
            <div className="mt-4 space-y-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-bold text-slate-900">
                  {format(calDate, 'EEEE d MMMM yyyy', { locale: it })}
                </p>
                {selectedEntry && Array.isArray(selectedEntry.blocked_slots) && selectedEntry.blocked_slots.length === 0 && (
                  <p className="text-xs text-red-600 mt-1 font-semibold">Giorno intero BLOCCATO</p>
                )}
                {selectedEntry?.blocked_slots?.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1 font-semibold">
                    Fasce bloccate: {selectedEntry.blocked_slots.join(', ')}
                  </p>
                )}
                {selectedEntry?.assigned_province && (
                  <p className="text-xs text-blue-600 mt-1 font-semibold flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Provincia: {selectedEntry.assigned_province.charAt(0).toUpperCase() + selectedEntry.assigned_province.slice(1)}
                  </p>
                )}
              </div>

              {/* Province Assignment */}
              <div className="bg-blue-50/50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Assegna Provincia
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => handleProvinceChange('')}
                    disabled={provinceProcessing}
                    className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                      !selectedEntry?.assigned_province
                        ? 'bg-slate-700 text-white border-slate-700'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                    } ${provinceProcessing ? 'opacity-50' : ''}`}
                    data-testid="province-btn-none"
                  >
                    Nessuna
                  </button>
                  {PROVINCES.map((p) => {
                    const pc = PROVINCE_COLORS[p];
                    const isActive = selectedEntry?.assigned_province === p;
                    return (
                      <button
                        key={p}
                        onClick={() => handleProvinceChange(p)}
                        disabled={provinceProcessing}
                        className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                          isActive
                            ? `text-white border-transparent`
                            : `bg-white border-slate-200 hover:border-slate-400`
                        } ${provinceProcessing ? 'opacity-50' : ''}`}
                        style={isActive ? { backgroundColor: pc.text, color: '#fff' } : { color: pc.text }}
                        data-testid={`province-btn-${p}`}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    );
                  })}
                </div>
                {provinceProcessing && <p className="text-[10px] text-blue-500 animate-pulse">Salvataggio...</p>}
                <p className="text-[10px] text-slate-400">Solo i clienti di questa provincia vedranno il giorno</p>
              </div>

              {/* Unblock full day */}
              {selectedEntry && Array.isArray(selectedEntry.blocked_slots) && selectedEntry.blocked_slots.length === 0 && (
                <Button
                  className="w-full rounded-lg text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleUnblock(format(calDate, 'yyyy-MM-dd'))}
                  data-testid="unblock-day-btn"
                >
                  Sblocca Giorno Intero
                </Button>
              )}

              {/* Block options (when NOT fully blocked) */}
              {!(selectedEntry && Array.isArray(selectedEntry.blocked_slots) && selectedEntry.blocked_slots.length === 0) && (
                <>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={blockMode === 'day' ? 'default' : 'outline'}
                      className="flex-1 rounded-lg text-xs"
                      onClick={() => { setBlockMode('day'); setSelectedSlots([]); }}
                      data-testid="mode-day"
                    >
                      Giorno intero
                    </Button>
                    <Button
                      size="sm"
                      variant={blockMode === 'slots' ? 'default' : 'outline'}
                      className="flex-1 rounded-lg text-xs"
                      onClick={() => setBlockMode('slots')}
                      data-testid="mode-slots"
                    >
                      Singole fasce
                    </Button>
                  </div>

                  {blockMode === 'slots' && (
                    <div className="grid grid-cols-2 gap-2">
                      {ALL_SLOTS.map((slot) => {
                        const alreadyBlocked = selectedEntry?.blocked_slots?.includes(slot);
                        const isSelected = selectedSlots.includes(slot);
                        return (
                          <div
                            key={slot}
                            onClick={() => !alreadyBlocked && toggleSlot(slot)}
                            className={`p-2.5 rounded-lg border-2 text-center cursor-pointer transition-all text-xs font-medium ${
                              alreadyBlocked
                                ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                                : isSelected
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-slate-100 hover:border-slate-200 text-slate-700'
                            }`}
                            data-testid={`slot-toggle-${slot}`}
                          >
                            <Clock className="w-3 h-3 mx-auto mb-1" />
                            {slot}
                            {alreadyBlocked && <p className="text-[9px] text-red-400 mt-0.5">bloccata</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Motivo (opzionale)"
                    className="rounded-lg text-sm"
                    data-testid="block-reason-input"
                  />

                  <Button
                    className="w-full rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleBlock}
                    disabled={processing || (blockMode === 'slots' && selectedSlots.length === 0)}
                    data-testid="block-btn"
                  >
                    {processing ? 'Attendi...' : blockMode === 'day' ? 'Blocca Giorno Intero' : `Blocca ${selectedSlots.length} fasce`}
                  </Button>
                </>
              )}
            </div>
          )}
          {calDate && !selectedIsValid && (
            <div className="mt-4 bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-600">Sabato/Domenica — non serve bloccarlo.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Date e fasce bloccate / Province assegnate</p>
        {blockedDates.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-8 text-center">
              <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Nessuna data configurata</p>
              <p className="text-xs text-slate-400 mt-1">Seleziona un giorno per bloccare fasce orarie o assegnare una provincia</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {blockedDates.map((d) => {
              const dateObj = new Date(d.date + 'T00:00:00');
              const isFullDay = Array.isArray(d.blocked_slots) && d.blocked_slots.length === 0;
              const hasSlots = d.blocked_slots && d.blocked_slots.length > 0;
              const prov = d.assigned_province || '';
              const provColor = PROVINCE_COLORS[prov] || null;
              const onlyProvince = prov && !isFullDay && !hasSlots;
              return (
                <Card key={d.date} className="border-0 shadow-sm rounded-xl" data-testid={`blocked-${d.date}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center ${
                          isFullDay ? 'bg-red-50' : hasSlots ? 'bg-amber-50' : 'bg-blue-50'
                        }`}>
                          <span className={`text-[10px] font-bold ${
                            isFullDay ? 'text-red-600' : hasSlots ? 'text-amber-600' : 'text-blue-600'
                          }`}>{dateObj.toLocaleDateString('it-IT', { month: 'short' })}</span>
                          <span className={`text-sm font-bold leading-tight ${
                            isFullDay ? 'text-red-700' : hasSlots ? 'text-amber-700' : 'text-blue-700'
                          }`}>{dateObj.getDate()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {dateObj.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {isFullDay && (
                              <Badge className="border-0 text-[10px] bg-red-50 text-red-700">Giorno intero</Badge>
                            )}
                            {hasSlots && (
                              <Badge className="border-0 text-[10px] bg-amber-50 text-amber-700">{d.blocked_slots.length} fasce</Badge>
                            )}
                            {prov && (
                              <Badge
                                className="border-0 text-[10px]"
                                style={provColor ? { backgroundColor: provColor.bg, color: provColor.text } : {}}
                              >
                                <MapPin className="w-2.5 h-2.5 mr-0.5 inline" />
                                {prov.charAt(0).toUpperCase() + prov.slice(1)}
                              </Badge>
                            )}
                            {d.reason && <span className="text-xs text-slate-400">{d.reason}</span>}
                          </div>
                        </div>
                      </div>
                      {isFullDay && (
                        <Button
                          variant="outline" size="sm"
                          className="rounded-lg text-xs border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleUnblock(d.date)}
                        >
                          Sblocca
                        </Button>
                      )}
                    </div>
                    {hasSlots && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {d.blocked_slots.map((slot) => (
                          <div key={slot} className="flex items-center gap-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5">
                            <Clock className="w-3 h-3 text-amber-600" />
                            <span className="text-xs font-medium text-amber-700">{slot}</span>
                            <button
                              onClick={() => handleUnblock(d.date, slot)}
                              className="ml-1 text-amber-400 hover:text-red-500 text-xs font-bold"
                              data-testid={`unblock-slot-${d.date}-${slot}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── QR Codes Panel ──
const QR_ITEMS = [
  { key: 'check_generale', label: 'Check Generale', desc: 'Porta il cliente direttamente al servizio Check Generale', color: 'bg-blue-500' },
  { key: 'intervento', label: 'Intervento a Chiamata', desc: 'Porta il cliente al servizio Intervento a Chiamata', color: 'bg-amber-500' },
  { key: 'abbonamento', label: 'Abbonamento', desc: 'Porta il cliente al servizio Abbonamento', color: 'bg-green-500' },
  { key: 'generale', label: 'Pagina Generale', desc: 'Porta il cliente alla landing page di REGOLA', color: 'bg-slate-700' },
];

function QrCodesPanel() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const downloadQr = (key, label) => {
    const link = document.createElement('a');
    link.href = `${apiBase}/api/qr/generate/${key}`;
    link.download = `qr_regola_${key}.png`;
    link.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <QrCode className="w-5 h-5 text-slate-700" />
        <div>
          <h3 className="text-sm font-bold text-slate-900">QR Code Marketing</h3>
          <p className="text-xs text-slate-500">Stampa i QR code per volantini, biglietti da visita e pubblicità. Ogni scansione viene tracciata.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {QR_ITEMS.map((q) => (
          <Card key={q.key} className="border-0 shadow-sm rounded-xl overflow-hidden" data-testid={`qr-card-${q.key}`}>
            <div className={`${q.color} px-4 py-2.5`}>
              <p className="text-white font-bold text-sm">{q.label}</p>
            </div>
            <CardContent className="p-4 flex flex-col items-center">
              <img
                src={`${apiBase}/api/qr/generate/${q.key}`}
                alt={`QR ${q.label}`}
                className="w-44 h-44 my-3"
                data-testid={`qr-img-${q.key}`}
              />
              <p className="text-xs text-slate-500 text-center mb-3">{q.desc}</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 text-xs"
                onClick={() => downloadQr(q.key, q.label)}
                data-testid={`qr-download-${q.key}`}
              >
                <Download className="w-3.5 h-3.5" /> Scarica PNG
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-0 shadow-sm rounded-xl bg-slate-50">
        <CardContent className="p-4">
          <p className="text-xs text-slate-600">
            <span className="font-semibold">Come funziona:</span> Quando un cliente scansiona il QR, viene tracciata la fonte (es. "qr_check_generale").
            Troverai la fonte nel tab <span className="font-semibold">Tutti Appuntamenti</span> con il badge viola "Fonte".
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Admin Dashboard ──
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [dailyData, setDailyData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [mapData, setMapData] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookingLinks, setBookingLinks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('daily');

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, aRes, qRes, cRes, nRes, mRes, bRes, blRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/appointments'),
          api.get('/admin/quotes'),
          api.get('/admin/clients'),
          api.get('/admin/notifications'),
          api.get('/admin/map-data'),
          api.get('/admin/blocked-dates'),
          api.get('/admin/booking-links'),
        ]);
        setStats(sRes.data);
        setAppointments(aRes.data);
        setQuotes(qRes.data);
        setClients(cRes.data);
        setNotifications(nRes.data);
        setMapData(mRes.data);
        setBlockedDates(bRes.data);
        setBookingLinks(blRes.data);
        setUnreadCount(sRes.data.unread_notifications || 0);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error('Accesso admin richiesto');
          navigate('/dashboard');
        } else {
          toast.error('Errore caricamento dati admin');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  // Poll unread notifications every 30 seconds
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await api.get('/admin/notifications/unread-count');
        setUnreadCount(res.data.unread_count || 0);
      } catch {}
    }, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    api.get(`/admin/appointments/daily?date=${dateStr}`)
      .then((r) => setDailyData(r.data))
      .catch(() => {});
  }, [selectedDate]);

  const updateStatus = async (aptId, status) => {
    try {
      await api.put(`/admin/appointments/${aptId}/status`, { status });
      setAppointments((prev) =>
        prev.map((a) => a.appointment_id === aptId ? { ...a, status } : a)
      );
      if (selectedDate) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const r = await api.get(`/admin/appointments/daily?date=${dateStr}`);
        setDailyData(r.data);
      }
      toast.success('Stato aggiornato');
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await api.put(`/admin/notifications/${notifId}/read`);
      setNotifications((prev) => prev.map((n) => n.notification_id === notifId ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/admin/notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('Tutte le notifiche segnate come lette');
    } catch {
      toast.error('Errore');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'notifications') {
      api.get('/admin/notifications').then((r) => setNotifications(r.data)).catch(() => {});
      api.get('/admin/notifications/unread-count').then((r) => setUnreadCount(r.data.unread_count || 0)).catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allDailyApts = dailyData ? Object.values(dailyData.zones).flat() : [];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.prod-images.emergentagent.com/jobs/b0feedf7-36df-41e9-9474-99c5d4f77205/images/70da477a4272b0edc6ede2f5e422bf85f189422131684b511c96d6b04c9062b9.jpeg" alt="REGOLA" className="h-8 brightness-0 invert" />
            <Badge className="bg-blue-600 text-white border-0 text-xs">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="admin-home-btn">
              <Home className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <button
              onClick={() => setActiveTab('notifications')}
              className="relative p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
              data-testid="admin-bell-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse" data-testid="unread-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="admin-user-menu">
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-blue-600 text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: 'Preventivi', value: stats.total_quotes, icon: ClipboardList, color: 'text-blue-600' },
              { label: 'Appuntamenti', value: stats.total_appointments, icon: CalendarDays, color: 'text-blue-600' },
              { label: 'Attivi', value: stats.active_appointments, icon: Clock, color: 'text-amber-500' },
              { label: 'Completati', value: stats.completed_appointments, icon: BarChart3, color: 'text-emerald-500' },
              { label: 'Clienti', value: stats.total_clients, icon: Users, color: 'text-violet-500' },
              { label: 'Notifiche', value: stats.total_notifications, icon: Bell, color: 'text-slate-500' },
            ].map((s, i) => (
              <Card key={i} className="border-0 shadow-sm rounded-xl" data-testid={`stat-${s.label.toLowerCase()}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <div>
                    <p className="text-2xl font-extrabold text-slate-900">{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-6 bg-white rounded-xl shadow-sm p-1">
            <TabsTrigger value="daily" className="rounded-lg text-xs" data-testid="tab-daily">Vista Giornaliera</TabsTrigger>
            <TabsTrigger value="tech" className="rounded-lg text-xs" data-testid="tab-tech">Riepilogo Tecnico</TabsTrigger>
            <TabsTrigger value="weekly" className="rounded-lg text-xs" data-testid="tab-weekly">Vista Settimanale</TabsTrigger>
            <TabsTrigger value="map" className="rounded-lg text-xs" data-testid="tab-map">Mappa Province</TabsTrigger>
            <TabsTrigger value="appointments" className="rounded-lg text-xs" data-testid="tab-all-apt">Tutti Appuntamenti</TabsTrigger>
            <TabsTrigger value="clients" className="rounded-lg text-xs" data-testid="tab-clients">Clienti</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-lg text-xs relative" data-testid="tab-notifs">
              Notifiche
              {unreadCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar-mgmt" className="rounded-lg text-xs" data-testid="tab-calendar-mgmt">Calendario</TabsTrigger>
            <TabsTrigger value="booking-links" className="rounded-lg text-xs" data-testid="tab-booking-links">Link Prenotazione</TabsTrigger>
            <TabsTrigger value="qr-codes" className="rounded-lg text-xs" data-testid="tab-qr-codes">QR Code</TabsTrigger>
          </TabsList>

          {/* ══ DAILY VIEW ══ */}
          <TabsContent value="daily">
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
              <Card className="border-0 shadow-sm rounded-xl h-fit">
                <CardContent className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={it}
                    className="w-full"
                    data-testid="admin-calendar"
                  />
                </CardContent>
              </Card>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {selectedDate ? format(selectedDate, 'EEEE d MMMM yyyy', { locale: it }) : 'Seleziona data'}
                  </h3>
                  {dailyData && (
                    <Badge className="bg-blue-50 text-blue-700 border-0">{dailyData.total} appuntamenti</Badge>
                  )}
                </div>

                {allDailyApts.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500">Clicca su un appuntamento per aprire appunti e costi</p>
                    {allDailyApts
                      .sort((a, b) => (a.time_slot || '').localeCompare(b.time_slot || ''))
                      .map((apt) => (
                        <AppointmentCard key={apt.appointment_id} apt={apt} onStatusChange={updateStatus} />
                      ))
                    }
                  </div>
                ) : (
                  <Card className="border-0 shadow-sm rounded-xl">
                    <CardContent className="p-8 text-center">
                      <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Nessun appuntamento per questa data</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ══ TECH VIEW ══ */}
          <TabsContent value="tech">
            <TechView />
          </TabsContent>

          {/* ══ WEEKLY VIEW ══ */}
          <TabsContent value="weekly">
            <WeeklyView />
          </TabsContent>

          {/* Map */}
          <TabsContent value="map">
            <ProvinceMap mapData={mapData} />
          </TabsContent>

          {/* All Appointments */}
          <TabsContent value="appointments">
            <div className="space-y-3">
              {appointments.length === 0 ? (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-slate-500">Nessun appuntamento</p>
                  </CardContent>
                </Card>
              ) : appointments.map((apt) => (
                <Card key={apt.appointment_id} className="border-0 shadow-sm rounded-xl" data-testid={`admin-apt-${apt.appointment_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex flex-col items-center justify-center">
                          <span className="text-[10px] text-blue-600 font-bold">{new Date(apt.date).toLocaleDateString('it-IT', { month: 'short' })}</span>
                          <span className="text-lg font-bold text-blue-700 leading-tight">{new Date(apt.date).getDate()}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-slate-900">{apt.client_name || 'Cliente'}</p>
                          <p className="text-xs text-slate-500">
                            <Clock className="w-3 h-3 inline mr-1" />{apt.time_slot}
                            <MapPin className="w-3 h-3 inline mx-1 ml-2" />{apt.zone || apt.province}
                          </p>
                          {apt.fonte && (
                            <Badge className="bg-violet-50 text-violet-700 border-0 text-[10px] mt-1">Fonte: {apt.fonte}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-slate-900">{apt.price_min}€</p>
                        <Badge className={`border-0 text-xs ${STATUS_MAP[apt.status]?.cls || 'bg-slate-50 text-slate-700'}`}>
                          {STATUS_MAP[apt.status]?.label || apt.status}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => updateStatus(apt.appointment_id, 'in_progress')}>In corso</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(apt.appointment_id, 'completed')}>Completato</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(apt.appointment_id, 'cancelled')} className="text-red-600">Annulla</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Clients */}
          <TabsContent value="clients">
            <div className="space-y-3">
              {clients.length === 0 ? (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-slate-500">Nessun cliente registrato</p>
                  </CardContent>
                </Card>
              ) : clients.map((c) => (
                <Card key={c.user_id} className="border-0 shadow-sm rounded-xl" data-testid={`client-${c.user_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={c.picture} />
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-bold">{c.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</p>
                    </div>
                    <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString('it-IT')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications">
            <div className="space-y-3">
              {notifications.length > 0 && unreadCount > 0 && (
                <div className="flex justify-end mb-2">
                  <Button variant="outline" size="sm" onClick={markAllRead} className="text-xs" data-testid="mark-all-read-btn">
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Segna tutte come lette
                  </Button>
                </div>
              )}
              {notifications.length === 0 ? (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-8 text-center">
                    <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">Nessuna notifica</p>
                  </CardContent>
                </Card>
              ) : notifications.map((n) => (
                <Card
                  key={n.notification_id}
                  className={`border-0 shadow-sm rounded-xl cursor-pointer transition-all ${!n.read ? 'bg-blue-50/60 ring-1 ring-blue-200' : ''}`}
                  onClick={() => !n.read && markNotificationRead(n.notification_id)}
                  data-testid={`notif-${n.notification_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0" data-testid="unread-dot" />}
                      <Badge className={`border-0 text-xs ${
                        n.type === 'booking' ? 'bg-green-50 text-green-700' :
                        n.type === 'sms' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        {n.type === 'booking' ? 'Prenotazione' : n.type === 'sms' ? 'SMS' : 'Email'}
                      </Badge>
                      <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString('it-IT')}</span>
                      {n.status === 'sent_mock' && <Badge className="bg-amber-50 text-amber-700 border-0 text-xs ml-auto">MOCK</Badge>}
                    </div>
                    {n.to && <p className="text-sm text-slate-600"><span className="font-medium">A:</span> {n.to}</p>}
                    {n.subject && <p className="text-sm text-slate-600"><span className="font-medium">Oggetto:</span> {n.subject}</p>}
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message || n.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Calendar Management */}
          <TabsContent value="calendar-mgmt">
            <CalendarManagement blockedDates={blockedDates} setBlockedDates={setBlockedDates} />
          </TabsContent>

          {/* Booking Links */}
          <TabsContent value="booking-links">
            <BookingLinksManager bookingLinks={bookingLinks} setBookingLinks={setBookingLinks} />
          </TabsContent>

          {/* QR Codes */}
          <TabsContent value="qr-codes">
            <QrCodesPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
